import {
  apiService,
  BatchVideoSegmentsTarget,
  MarkerInfo,
  PromptRun,
  VideoSegmentsResponse,
} from './api';
import { pickMediaUri } from '../utils/mediaSources';

export interface SegmentData {
  segment_id: string;
  video_id?: string;
  start_time: number;
  end_time: number;
  processed: boolean;
  processing_failed: boolean;
  segment_status?: 'pending' | 'processing' | 'successful' | 'failed';
  failure_stage?: string | null;
  failure_message?: string | null;
  attempt_id?: string | null;
  status_source?: string | null;
  metadata?: Record<string, any>;
  /** True only after a durable segment-result publication, not checkpoint projection. */
  metadata_available?: boolean;
  metadata_text?: string;
  processing_warning?: string;
  error_message?: string;
  segment_uri?: string;
  gcs_uri?: string;
  playback_source_uri?: string | null;
  playback_start_time?: number | null;
  playback_end_time?: number | null;
  thumbnail_gcs_uri?: string;
  gif_gcs_uri?: string;
  thumbnail_uri?: string;
  gif_uri?: string;
  thumbnail_data?: string;
  thumbnail_available?: boolean;
  gif_data?: string;
  gif_available?: boolean;
  field_extraction_succeeded?: boolean;
  image_embedding_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'not_applicable' | null;
  field_extraction_error?: string;
  image_embedding_error?: string;
  marker?: MarkerInfo;
  metadata_markers?: Record<string, MarkerInfo>;
  from_run_id?: string;
}

interface CacheEntry<T> {
  value: T;
  timestamp: number;
}

export type SegmentProjectionCompleteness = 'partial' | 'complete';

interface SegmentCacheEntry extends CacheEntry<SegmentData[]> {
  completeness: SegmentProjectionCompleteness;
  /** Segment ids hydrated by an exact/full detail projection. */
  detailHydratedSegmentIds: Set<string>;
}

export interface PrimeSegmentProjectionOptions {
  completeness?: SegmentProjectionCompleteness;
  projection?: 'gallery' | 'detail' | 'full';
}

export interface LoadBatchSegmentsOptions {
  maxAttempts?: number;
  retryDelayMs?: number;
  completeness?: SegmentProjectionCompleteness;
  /** Prevents a newer dataset generation from joining an older in-flight request. */
  requestOwnerKey?: string;
}

interface InflightBatchRequest {
  generation: number;
  request: Promise<VideoSegmentsResponse[]>;
}

const CACHE_TTL_MS = 10 * 60 * 1000;
const MAX_SEGMENT_SCOPES = 100;
const MAX_PROMPT_RUN_VIDEOS = 100;
const MAX_BATCH_SEGMENT_TARGETS = 100;
class SegmentCacheService {
  private readonly segmentCache = new Map<string, SegmentCacheEntry>();
  private readonly promptRunCache = new Map<string, CacheEntry<PromptRun[]>>();
  private readonly resultRevisions = new Map<string, string>();
  private readonly segmentRequests = new Map<string, Promise<SegmentData[]>>();
  private readonly promptRunRequests = new Map<string, Promise<PromptRun[]>>();
  private readonly batchSegmentRequests = new Map<string, InflightBatchRequest>();
  private readonly segmentVideoGenerations = new Map<string, number>();
  private readonly promptRunVideoGenerations = new Map<string, number>();
  private cacheGeneration = 0;

  private getVideoGeneration(
    generations: Map<string, number>,
    videoId: string,
  ): number {
    return generations.get(videoId) || 0;
  }

  private bumpVideoGeneration(
    generations: Map<string, number>,
    videoId: string,
  ): void {
    generations.set(videoId, this.getVideoGeneration(generations, videoId) + 1);
  }

  private getSegmentVideoGeneration(videoId: string): number {
    return this.getVideoGeneration(this.segmentVideoGenerations, videoId);
  }

  private isSegmentGenerationCurrent(
    videoId: string,
    expectedCacheGeneration: number,
    expectedVideoGeneration: number,
  ): boolean {
    return (
      expectedCacheGeneration === this.cacheGeneration
      && expectedVideoGeneration === this.getSegmentVideoGeneration(videoId)
    );
  }

  private getSegmentCacheKey(videoId: string, runId: string | null): string {
    return runId ? `${videoId}_run_${runId}` : `${videoId}_base`;
  }

  private normalizeSegmentMedia(segment: SegmentData): SegmentData {
    const segmentUri = pickMediaUri(segment.segment_uri, segment.gcs_uri);
    return {
      ...segment,
      segment_uri: segmentUri || undefined,
      // Preview grants have one bounded owner in SearchWorkspaceContext.
      // Durable segment metadata must never become a second signed-URL cache.
      thumbnail_uri: undefined,
      gif_uri: undefined,
      thumbnail_data: undefined,
      gif_data: undefined,
      thumbnail_available: Boolean(segment.thumbnail_gcs_uri),
      gif_available: Boolean(segment.gif_gcs_uri),
    };
  }

  private readFreshSegmentEntry(key: string): SegmentCacheEntry | null {
    const entry = this.segmentCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp >= CACHE_TTL_MS) {
      this.segmentCache.delete(key);
      return null;
    }
    this.segmentCache.delete(key);
    this.segmentCache.set(key, entry);
    return entry;
  }

  private mergeDefinedSegmentFields(
    current: SegmentData | undefined,
    projection: SegmentData,
  ): SegmentData {
    if (!current) return this.normalizeSegmentMedia(projection);
    const definedProjection = Object.fromEntries(
      Object.entries(projection).filter(([field, value]) => (
        value !== undefined
        && !(
          (field === 'segment_uri' || field === 'gcs_uri')
          && (value === null || (typeof value === 'string' && value.trim() === ''))
        )
      )),
    ) as Partial<SegmentData>;
    return this.normalizeSegmentMedia({ ...current, ...definedProjection });
  }

  private normalizeBatchTargets(targets: BatchVideoSegmentsTarget[]): BatchVideoSegmentsTarget[] {
    return Array.from(new Map(
      (targets || [])
        .filter((target) => Boolean(String(target.video_id || '').trim()))
        .flatMap((target) => {
          const segmentIds = Array.from(new Set(
            (target.segment_ids || [])
              .map((segmentId) => String(segmentId || '').trim())
              .filter(Boolean),
          )).sort();
          const segmentIdChunks: Array<string[] | undefined> = segmentIds.length > 0
            ? Array.from(
                { length: Math.ceil(segmentIds.length / MAX_BATCH_SEGMENT_TARGETS) },
                (_, index) => segmentIds.slice(
                  index * MAX_BATCH_SEGMENT_TARGETS,
                  (index + 1) * MAX_BATCH_SEGMENT_TARGETS,
                ),
              )
            : [undefined];
          return segmentIdChunks.map((segmentIdChunk): [string, BatchVideoSegmentsTarget] => {
            const normalized: BatchVideoSegmentsTarget = {
              video_id: String(target.video_id).trim(),
              run_id: String(target.run_id || '').trim() || undefined,
              segment_ids: segmentIdChunk,
              cursor: String(target.cursor || '').trim() || undefined,
              limit: Number.isInteger(target.limit) && Number(target.limit) > 0
                ? Math.min(Number(target.limit), MAX_BATCH_SEGMENT_TARGETS)
                : undefined,
            };
            const targetKey = [
              normalized.video_id,
              normalized.run_id || 'latest',
              (normalized.segment_ids || []).join(','),
              normalized.cursor || '',
              normalized.limit || '',
            ].join('::');
            return [targetKey, normalized];
          });
        }),
    ).values()).sort((left, right) => (
      JSON.stringify(left).localeCompare(JSON.stringify(right))
    ));
  }

  private isRetryableBatchError(error: unknown): boolean {
    const candidate = error as {
      name?: string;
      code?: string;
      response?: { status?: number };
    } | null;
    if (candidate?.name === 'AbortError' || candidate?.code === 'ERR_CANCELED') {
      return false;
    }
    const status = Number(candidate?.response?.status);
    if (!Number.isFinite(status) || status <= 0) {
      return true;
    }
    return status === 408 || status === 425 || status === 429 || status >= 500;
  }

  private readFresh<T>(cache: Map<string, CacheEntry<T>>, key: string): T | null {
    const entry = cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp >= CACHE_TTL_MS) {
      cache.delete(key);
      return null;
    }
    // Refresh insertion order for bounded least-recently-used eviction.
    cache.delete(key);
    cache.set(key, entry);
    return entry.value;
  }

  private writeBounded<T>(
    cache: Map<string, CacheEntry<T>>,
    key: string,
    value: T,
    maxEntries: number,
  ): void {
    cache.delete(key);
    cache.set(key, { value, timestamp: Date.now() });
    while (cache.size > maxEntries) {
      const oldestKey = cache.keys().next().value;
      if (oldestKey === undefined) break;
      cache.delete(oldestKey);
    }
  }

  async getCachedSegments(videoId: string, runId: string | null): Promise<SegmentData[] | null> {
    return this.peekSegments(videoId, runId);
  }

  /** Synchronous cache-first read for render/load orchestration. */
  peekSegments(
    videoId: string,
    runId: string | null,
    options: { requireComplete?: boolean } = {},
  ): SegmentData[] | null {
    const entry = this.readFreshSegmentEntry(this.getSegmentCacheKey(videoId, runId));
    if (!entry || (options.requireComplete && entry.completeness !== 'complete')) {
      return null;
    }
    return entry.value;
  }

  peekSegment(
    videoId: string,
    runId: string | null,
    segmentId: string,
  ): SegmentData | null {
    return this.peekSegments(videoId, runId)?.find(
      (segment) => segment.segment_id === segmentId,
    ) || null;
  }

  /**
   * Read a segment only when its metadata/detail fields are authoritative.
   * Gallery summaries deliberately do not satisfy this boundary.
   */
  peekDetailSegment(
    videoId: string,
    runId: string | null,
    segmentId: string,
  ): SegmentData | null {
    const entry = this.readFreshSegmentEntry(this.getSegmentCacheKey(videoId, runId));
    if (
      !entry
      || (
        entry.completeness !== 'complete'
        && !entry.detailHydratedSegmentIds.has(segmentId)
      )
    ) return null;
    return entry.value.find((segment) => segment.segment_id === segmentId) || null;
  }

  async getCachedPromptRuns(videoId: string): Promise<PromptRun[] | null> {
    return this.readFresh(this.promptRunCache, videoId);
  }

  async cacheSegments(
    videoId: string,
    runId: string | null,
    segments: SegmentData[],
    expectedGeneration = this.cacheGeneration,
    expectedVideoGeneration = this.getSegmentVideoGeneration(videoId),
  ): Promise<void> {
    this.primeSegmentProjection(
      videoId,
      runId,
      segments,
      { completeness: 'complete' },
      expectedGeneration,
      expectedVideoGeneration,
    );
  }

  /**
   * Prime a full response or a media-only projection without allowing a
   * partial projection to satisfy callers that require the complete segment
   * list. Partial projections merge by segment id and never erase defined
   * fields from a richer cached record.
   */
  primeSegmentProjection(
    videoId: string,
    runId: string | null,
    segments: SegmentData[],
    options: PrimeSegmentProjectionOptions = {},
    expectedGeneration = this.cacheGeneration,
    expectedVideoGeneration = this.getSegmentVideoGeneration(videoId),
  ): void {
    if (!this.isSegmentGenerationCurrent(
      videoId,
      expectedGeneration,
      expectedVideoGeneration,
    )) return;
    const cacheKey = this.getSegmentCacheKey(videoId, runId);
    const completeness = options.completeness || 'partial';
    const existing = this.readFreshSegmentEntry(cacheKey);
    let nextSegments: SegmentData[];
    let nextCompleteness = completeness;
    const nextDetailHydratedSegmentIds = new Set(
      existing?.detailHydratedSegmentIds || [],
    );
    if (options.projection === 'detail' || options.projection === 'full') {
      (segments || []).forEach((segment) => {
        if (segment.segment_id) nextDetailHydratedSegmentIds.add(segment.segment_id);
      });
    }

    if (completeness === 'complete') {
      nextSegments = (segments || []).map((segment) => this.normalizeSegmentMedia(segment));
    } else {
      const merged = new Map<string, SegmentData>();
      (existing?.value || []).forEach((segment) => merged.set(segment.segment_id, segment));
      (segments || []).forEach((segment) => {
        merged.set(
          segment.segment_id,
          this.mergeDefinedSegmentFields(merged.get(segment.segment_id), segment),
        );
      });
      nextSegments = Array.from(merged.values());
      if (existing?.completeness === 'complete') {
        nextCompleteness = 'complete';
      }
    }

    this.writeBounded(
      this.segmentCache,
      cacheKey,
      nextSegments,
      MAX_SEGMENT_SCOPES,
    );
    const written = this.segmentCache.get(cacheKey);
    if (written) {
      this.segmentCache.set(cacheKey, {
        ...written,
        completeness: nextCompleteness,
        detailHydratedSegmentIds: nextDetailHydratedSegmentIds,
      });
    }
  }

  primeBatchSegmentResponses(
    targets: BatchVideoSegmentsTarget[],
    responses: VideoSegmentsResponse[],
    options: PrimeSegmentProjectionOptions = { completeness: 'complete' },
    expectedGeneration = this.cacheGeneration,
    expectedVideoGenerations?: ReadonlyMap<string, number>,
  ): void {
    if (expectedGeneration !== this.cacheGeneration) return;
    const normalizedTargets = this.normalizeBatchTargets(targets);
    (responses || []).forEach((response) => {
      const expectedVideoGeneration = expectedVideoGenerations?.get(response.video_id)
        ?? this.getSegmentVideoGeneration(response.video_id);
      if (!this.isSegmentGenerationCurrent(
        response.video_id,
        expectedGeneration,
        expectedVideoGeneration,
      )) return;
      const segmentRunIds = new Set(
        (response.segments || [])
          .map((segment) => String(segment.from_run_id || '').trim())
          .filter(Boolean),
      );
      const inferredRunId = response.run_id
        || (segmentRunIds.size === 1 ? Array.from(segmentRunIds)[0] : null);
      const videoTargets = normalizedTargets.filter(
        (target) => target.video_id === response.video_id,
      );
      const matchingTargets = inferredRunId
        ? videoTargets.filter((target) => !target.run_id || target.run_id === inferredRunId)
        : videoTargets.length === 1
          ? videoTargets
          : videoTargets.filter((target) => !target.run_id);
      const scopes = new Set<string | null>();
      matchingTargets.forEach((target) => scopes.add(target.run_id || null));
      if (inferredRunId) scopes.add(inferredRunId);
      if (scopes.size === 0) scopes.add(null);

      const requestedCompleteness = options.completeness || 'complete';
      const completeness = response.has_more ? 'partial' : requestedCompleteness;
      scopes.forEach((runId) => {
        this.primeSegmentProjection(
          response.video_id,
          runId,
          response.segments as SegmentData[],
          { completeness, projection: options.projection },
          expectedGeneration,
          expectedVideoGeneration,
        );
      });
    });
  }

  /** Shared batch single-flight with bounded retries for transient failures. */
  async loadBatchSegments(
    targets: BatchVideoSegmentsTarget[],
    options: LoadBatchSegmentsOptions = {},
  ): Promise<VideoSegmentsResponse[]> {
    const normalizedTargets = this.normalizeBatchTargets(targets);
    if (normalizedTargets.length === 0) return [];
    const requestGeneration = this.cacheGeneration;
    const requestVideoGenerations = new Map<string, number>();
    normalizedTargets.forEach((target) => {
      requestVideoGenerations.set(
        target.video_id,
        this.getSegmentVideoGeneration(target.video_id),
      );
    });
    const videoGenerationKey = Array.from(requestVideoGenerations.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([videoId, generation]) => `${videoId}:${generation}`)
      .join('|');
    const requestKey = [
      requestGeneration,
      videoGenerationKey,
      options.completeness || 'default',
      options.requestOwnerKey || 'unowned',
      normalizedTargets
      .map((target) => JSON.stringify(target))
      .join('|'),
    ].join('::');
    const existing = this.batchSegmentRequests.get(requestKey);
    if (existing?.generation === requestGeneration) {
      return existing.request;
    }

    const networkTargets = normalizedTargets;

    const maxAttempts = Math.max(1, Math.min(options.maxAttempts ?? 3, 5));
    const retryDelayMs = Math.max(0, options.retryDelayMs ?? 150);
    const isGalleryTarget = (target: BatchVideoSegmentsTarget): boolean => (
      Boolean(target.segment_ids?.length) || Boolean(target.cursor) || target.limit !== undefined
    );
    const fullTargets = networkTargets.filter((target) => !isGalleryTarget(target));
    const galleryTargets = networkTargets.filter(isGalleryTarget);
    const isVideoCurrent = (videoId: string): boolean => (
      requestVideoGenerations.has(videoId)
      && this.isSegmentGenerationCurrent(
        videoId,
        requestGeneration,
        requestVideoGenerations.get(videoId) as number,
      )
    );

    const loadProjection = async (
      projectionTargets: BatchVideoSegmentsTarget[],
      projection: 'gallery' | 'full',
    ): Promise<VideoSegmentsResponse[]> => {
      const orderedResponses: VideoSegmentsResponse[] = [];
      for (
        let offset = 0;
        offset < projectionTargets.length;
        offset += MAX_BATCH_SEGMENT_TARGETS
      ) {
        const targetChunk = projectionTargets.slice(
          offset,
          offset + MAX_BATCH_SEGMENT_TARGETS,
        );
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          if (requestGeneration !== this.cacheGeneration) return [];
          const currentTargets = targetChunk.filter((target) => (
            isVideoCurrent(target.video_id)
          ));
          if (currentTargets.length === 0) break;
          try {
            const responses = await apiService.getBatchVideoSegments(
              currentTargets,
              projection === 'gallery' ? { view: 'gallery' } : undefined,
            );
            if (requestGeneration !== this.cacheGeneration) return [];
            const currentResponses = responses.filter((response) => (
              isVideoCurrent(response.video_id)
            ));
            this.primeBatchSegmentResponses(
              currentTargets,
              currentResponses,
              {
                completeness: projection === 'gallery'
                  ? 'partial'
                  : options.completeness || 'complete',
                projection,
              },
              requestGeneration,
              requestVideoGenerations,
            );
            orderedResponses.push(...currentResponses);
            break;
          } catch (error) {
            if (requestGeneration !== this.cacheGeneration) return [];
            if (!targetChunk.some((target) => isVideoCurrent(target.video_id))) {
              break;
            }
            if (attempt >= maxAttempts || !this.isRetryableBatchError(error)) {
              throw error;
            }
            await new Promise<void>((resolve) => {
              setTimeout(resolve, retryDelayMs * attempt);
            });
          }
        }
      }
      return orderedResponses.filter((response) => isVideoCurrent(response.video_id));
    };

    let request: Promise<VideoSegmentsResponse[]>;
    request = (async () => {
      const [fullResponses, galleryResponses] = await Promise.all([
        loadProjection(fullTargets, 'full'),
        loadProjection(galleryTargets, 'gallery'),
      ]);
      return [
        ...fullResponses,
        ...galleryResponses,
      ].filter((response) => isVideoCurrent(response.video_id));
    })().finally(() => {
      const current = this.batchSegmentRequests.get(requestKey);
      if (current?.request === request) {
        this.batchSegmentRequests.delete(requestKey);
      }
    });
    this.batchSegmentRequests.set(requestKey, {
      generation: requestGeneration,
      request,
    });
    return request;
  }

  async cachePromptRuns(
    videoId: string,
    runs: PromptRun[],
    expectedGeneration = this.cacheGeneration,
    expectedVideoGeneration = this.getVideoGeneration(
      this.promptRunVideoGenerations,
      videoId,
    ),
  ): Promise<void> {
    if (
      expectedGeneration !== this.cacheGeneration
      || expectedVideoGeneration !== this.getVideoGeneration(
        this.promptRunVideoGenerations,
        videoId,
      )
    ) return;
    this.writeBounded(this.promptRunCache, videoId, runs || [], MAX_PROMPT_RUN_VIDEOS);
  }

  observeResultRevision(videoId: string, runId: string | null, revision: string): void {
    const key = this.getSegmentCacheKey(videoId, runId);
    if (this.resultRevisions.get(key) === revision) return;
    this.resultRevisions.set(key, revision);
    this.segmentCache.delete(key);
    this.bumpVideoGeneration(this.segmentVideoGenerations, videoId);
  }

  async loadSegments(
    videoId: string,
    runId: string | null = null,
    forceRefresh = false,
  ): Promise<SegmentData[]> {
    const cacheKey = this.getSegmentCacheKey(videoId, runId);
    if (forceRefresh) {
      this.segmentCache.delete(cacheKey);
    } else {
      const cached = this.peekSegments(videoId, runId, { requireComplete: true });
      if (cached) return cached;
    }

    const existingRequest = this.segmentRequests.get(cacheKey);
    if (existingRequest) return existingRequest;

    const requestGeneration = this.cacheGeneration;
    const requestVideoGeneration = this.getSegmentVideoGeneration(videoId);
    const requestStartedAt = Date.now();
    const requestedRevision = this.resultRevisions.get(cacheKey);
    let request: Promise<SegmentData[]>;
    request = apiService.getAllVideoSegments(videoId, runId)
      .then((segments) => {
        // A newer publication while this request was in flight owns the next
        // read. All callers join that one follow-up instead of caching stale data.
        if (this.resultRevisions.get(cacheKey) !== requestedRevision) {
          if (this.segmentRequests.get(cacheKey) === request) this.segmentRequests.delete(cacheKey);
          return this.loadSegments(videoId, runId, true);
        }
        const normalized = (segments || []).map((segment) => (
          this.normalizeSegmentMedia(segment as SegmentData)
        ));
        if (this.isSegmentGenerationCurrent(
          videoId,
          requestGeneration,
          requestVideoGeneration,
        )) {
          const externallyPrimed = this.readFreshSegmentEntry(cacheKey);
          if (
            externallyPrimed?.completeness === 'complete'
            && externallyPrimed.timestamp > requestStartedAt
          ) {
            return externallyPrimed.value;
          }
          this.writeBounded(this.segmentCache, cacheKey, normalized, MAX_SEGMENT_SCOPES);
          const written = this.segmentCache.get(cacheKey);
          if (written) {
            this.segmentCache.set(cacheKey, {
              ...written,
              completeness: 'complete',
              detailHydratedSegmentIds: new Set(
                externallyPrimed?.detailHydratedSegmentIds || [],
              ),
            });
          }
        }
        return normalized;
      })
      .finally(() => {
        if (this.segmentRequests.get(cacheKey) === request) {
          this.segmentRequests.delete(cacheKey);
        }
      });
    this.segmentRequests.set(cacheKey, request);
    return request;
  }

  async loadPromptRuns(videoId: string, forceRefresh = false): Promise<PromptRun[]> {
    if (forceRefresh) {
      this.promptRunCache.delete(videoId);
    } else {
      const cached = await this.getCachedPromptRuns(videoId);
      if (cached) return cached;
    }

    const existingRequest = this.promptRunRequests.get(videoId);
    if (existingRequest) return existingRequest;

    const requestGeneration = this.cacheGeneration;
    const requestVideoGeneration = this.getVideoGeneration(
      this.promptRunVideoGenerations,
      videoId,
    );
    let request: Promise<PromptRun[]>;
    request = apiService.getPromptRunsByVideo(videoId)
      .then((runs) => {
        const normalizedRuns = runs || [];
        if (
          requestGeneration === this.cacheGeneration
          && requestVideoGeneration === this.getVideoGeneration(
            this.promptRunVideoGenerations,
            videoId,
          )
        ) {
          this.writeBounded(
            this.promptRunCache,
            videoId,
            normalizedRuns,
            MAX_PROMPT_RUN_VIDEOS,
          );
        }
        return normalizedRuns;
      })
      .finally(() => {
        if (this.promptRunRequests.get(videoId) === request) {
          this.promptRunRequests.delete(videoId);
        }
      });
    this.promptRunRequests.set(videoId, request);
    return request;
  }

  async clearVideoCache(videoId: string): Promise<void> {
    this.bumpVideoGeneration(this.segmentVideoGenerations, videoId);
    this.bumpVideoGeneration(this.promptRunVideoGenerations, videoId);
    Array.from(this.segmentCache.keys()).forEach((key) => {
      if (key === `${videoId}_base` || key.startsWith(`${videoId}_run_`)) {
        this.segmentCache.delete(key);
      }
    });
    this.promptRunCache.delete(videoId);
    Array.from(this.segmentRequests.keys()).forEach((key) => {
      if (key === `${videoId}_base` || key.startsWith(`${videoId}_run_`)) {
        this.segmentRequests.delete(key);
      }
    });
    this.promptRunRequests.delete(videoId);
  }

  async clearVideoSegments(videoId: string): Promise<void> {
    this.bumpVideoGeneration(this.segmentVideoGenerations, videoId);
    Array.from(this.segmentCache.keys()).forEach((key) => {
      if (key === `${videoId}_base` || key.startsWith(`${videoId}_run_`)) {
        this.segmentCache.delete(key);
      }
    });
    Array.from(this.segmentRequests.keys()).forEach((key) => {
      if (key === `${videoId}_base` || key.startsWith(`${videoId}_run_`)) {
        this.segmentRequests.delete(key);
      }
    });
  }

  async clearSegmentScope(videoId: string, runId: string | null): Promise<void> {
    // Advance the video generation so an older request for this scope cannot
    // repopulate it after invalidation. Other completed run caches remain
    // available; only the event's exact run/base scope is evicted.
    this.bumpVideoGeneration(this.segmentVideoGenerations, videoId);
    const cacheKey = this.getSegmentCacheKey(videoId, runId);
    this.segmentCache.delete(cacheKey);
    this.segmentRequests.delete(cacheKey);
  }

  async patchCachedSegment(
    videoId: string,
    runId: string | null,
    segmentId: string,
    patch: Partial<SegmentData>,
  ): Promise<void> {
    const applyMarkerAcrossRuns = Object.prototype.hasOwnProperty.call(patch, 'marker');
    this.segmentCache.forEach((entry, key) => {
      if (key !== `${videoId}_base` && !key.startsWith(`${videoId}_run_`)) return;
      const cachedRunId = key === `${videoId}_base`
        ? null
        : key.slice(`${videoId}_run_`.length);
      const index = entry.value.findIndex((segment) => segment.segment_id === segmentId);
      if (index < 0) return;
      const effectivePatch = cachedRunId === runId
        ? patch
        : applyMarkerAcrossRuns
          ? { marker: patch.marker }
          : null;
      if (!effectivePatch) return;
      const next = [...entry.value];
      next[index] = this.normalizeSegmentMedia({ ...next[index], ...effectivePatch });
      this.segmentCache.set(key, { ...entry, value: next });
    });
  }

  async patchCachedPromptRun(
    videoId: string,
    runId: string,
    patch: Partial<PromptRun>,
  ): Promise<void> {
    const entry = this.promptRunCache.get(videoId);
    if (!entry) return;
    const index = entry.value.findIndex((run) => run.run_id === runId);
    if (index < 0) return;
    const runs = [...entry.value];
    runs[index] = { ...runs[index], ...patch };
    this.promptRunCache.set(videoId, { ...entry, value: runs });
  }

  async clearAll(): Promise<void> {
    this.cacheGeneration += 1;
    this.segmentCache.clear();
    this.resultRevisions.clear();
    this.promptRunCache.clear();
    this.segmentRequests.clear();
    this.batchSegmentRequests.clear();
    this.promptRunRequests.clear();
    this.segmentVideoGenerations.clear();
    this.promptRunVideoGenerations.clear();
  }

  getCacheStats(): { segmentCaches: number; promptRunCaches: number; totalSegments: number } {
    let totalSegments = 0;
    this.segmentCache.forEach((entry) => {
      totalSegments += entry.value.length;
    });
    return {
      segmentCaches: this.segmentCache.size,
      promptRunCaches: this.promptRunCache.size,
      totalSegments,
    };
  }
}

export const segmentCache = new SegmentCacheService();
